/**
 * Diretivas de filtro de entrada.
 *
 * `v-apenas-numeros`      - permite somente dígitos (0-9).
 * `v-apenas-alfanumerico` - permite letras e dígitos, sem símbolos.
 *
 * Ambas atuam na digitação, na colagem e em preenchimentos automáticos
 * (autofill, arrastar e soltar).
 *
 * Uso: <InputText v-model="banco_agencia" v-apenas-numeros />
 */

const obterInput = (el) => (el instanceof HTMLInputElement ? el : el.querySelector('input'));

const aplicarValor = (input, valor, cursor) => {
    input.value = valor;
    if (input.setSelectionRange) {
        const posicao = Math.max(0, Math.min(cursor, valor.length));
        input.setSelectionRange(posicao, posicao);
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Cria uma diretiva que restringe o campo aos caracteres permitidos.
 *
 * @param {RegExp} permitido - casa com um único caractere aceito
 * @param {RegExp} proibido - casa com os caracteres a remover de um texto
 * @param {string|null} inputmode - teclado sugerido no mobile
 */
const criarFiltro = (permitido, proibido, inputmode = null) => {
    const aoDigitar = (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (event.key.length > 1) return;
        if (!permitido.test(event.key)) event.preventDefault();
    };

    const aoColar = (event) => {
        const input = obterInput(event.currentTarget);
        if (!input) return;

        event.preventDefault();

        const texto = ((event.clipboardData || window.clipboardData)?.getData('text') || '').replace(proibido, '');
        const inicio = input.selectionStart ?? input.value.length;
        const fim = input.selectionEnd ?? input.value.length;

        let valor = input.value.slice(0, inicio) + texto + input.value.slice(fim);
        if (input.maxLength > 0) valor = valor.slice(0, input.maxLength);

        aplicarValor(input, valor, inicio + texto.length);
    };

    const aoAlterar = (event) => {
        const input = obterInput(event.currentTarget);
        if (!input) return;

        const limpo = input.value.replace(proibido, '');
        if (limpo === input.value) return;

        const cursor = (input.selectionStart ?? input.value.length) - (input.value.length - limpo.length);
        aplicarValor(input, limpo, cursor);
    };

    return {
        mounted(el) {
            const input = obterInput(el);
            if (!input) return;

            if (inputmode) input.setAttribute('inputmode', inputmode);
            input.addEventListener('keypress', aoDigitar);
            input.addEventListener('paste', aoColar);
            input.addEventListener('input', aoAlterar);
        },
        unmounted(el) {
            const input = obterInput(el);
            if (!input) return;

            input.removeEventListener('keypress', aoDigitar);
            input.removeEventListener('paste', aoColar);
            input.removeEventListener('input', aoAlterar);
        },
    };
};

export const apenasNumeros = criarFiltro(/^\d$/, /\D/g, 'numeric');

export const apenasAlfanumerico = criarFiltro(/^[A-Za-z0-9]$/, /[^A-Za-z0-9]/g);
